import type { AdminClient, IntegratorKpiRow } from '@apx/admin-sdk';
import { emptyState } from '../empty-state.js';
import { skeletonTable } from '../skeleton.js';

export interface OverviewPageDeps {
  client: AdminClient;
}

type SortKey = 'name' | 'ggr' | 'turnover' | 'rounds' | 'players' | 'rtp';

let lastSort: { key: SortKey; dir: 'asc' | 'desc' } = { key: 'ggr', dir: 'desc' };

export async function renderOverviewPage(root: HTMLElement, deps: OverviewPageDeps): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Provider overview</h1>
    <p class="page-sub">All integrators ranked by last-24h activity. Click a row to drill into that integrator.</p>

    <div id="ov-totals" class="kpi-grid"></div>

    <div class="card">
      <h2>Integrators — last 24h</h2>
      <div class="row" style="margin-bottom:8px">
        <input id="ov-filter" type="search" placeholder="Filter by name or slug…" style="flex:1;max-width:320px" />
        <button id="ov-refresh" class="ghost">Refresh</button>
        <span id="ov-msg" class="muted"></span>
      </div>
      <div id="ov-list">${skeletonTable({ rows: 6, columns: 8 })}</div>
    </div>

    <style>
      /* KPI card styling is canonical in style.css (.kpi/.kpi-grid) so every
         scope's metric strip stays identical. Only page-local table chrome
         lives here. */
      .ov-row { cursor: pointer; }
      .ov-row:hover td { background: var(--panel-2); }
      .ov-th-sort { cursor: pointer; user-select: none; }
      .ov-th-sort:hover { color: var(--accent-2); }
      .ov-th-sort.active { color: var(--accent); }
      .ov-th-sort.active::after { content: ''; }
      .ov-th-sort.active.asc::after { content: ' ↑'; }
      .ov-th-sort.active.desc::after { content: ' ↓'; }
    </style>
  `;

  const listEl = root.querySelector<HTMLElement>('#ov-list')!;
  const totalsEl = root.querySelector<HTMLElement>('#ov-totals')!;
  const msgEl = root.querySelector<HTMLElement>('#ov-msg')!;
  const filterEl = root.querySelector<HTMLInputElement>('#ov-filter')!;
  const refreshBtn = root.querySelector<HTMLButtonElement>('#ov-refresh')!;

  let rows: IntegratorKpiRow[] = [];

  async function load(): Promise<void> {
    msgEl.textContent = 'Loading…';
    try {
      rows = await deps.client.listIntegratorsSummary();
      msgEl.textContent = '';
      renderTotals(totalsEl, rows);
      renderTable();
    } catch (err) {
      msgEl.textContent = err instanceof Error ? err.message : String(err);
      listEl.innerHTML = `<p class="error">Failed to load integrators.</p>`;
    }
  }

  function renderTable(): void {
    const filter = filterEl.value.trim().toLowerCase();
    const filtered = filter
      ? rows.filter(
          (r) => r.name.toLowerCase().includes(filter) || r.slug.toLowerCase().includes(filter),
        )
      : rows.slice();
    sortRows(filtered, lastSort.key, lastSort.dir);
    listEl.innerHTML = renderTable_(filtered);
    bindRowClicks();
    bindSortHeaders();
  }

  function bindRowClicks(): void {
    listEl.querySelectorAll<HTMLElement>('tr.ov-row').forEach((tr) => {
      tr.addEventListener('click', () => {
        const tid = tr.dataset.integrator;
        if (tid) window.location.href = `/integrators/${tid}/home`;
      });
    });
  }

  function bindSortHeaders(): void {
    listEl.querySelectorAll<HTMLElement>('.ov-th-sort').forEach((th) => {
      th.addEventListener('click', () => {
        const key = th.dataset.sort as SortKey;
        if (lastSort.key === key) {
          lastSort.dir = lastSort.dir === 'asc' ? 'desc' : 'asc';
        } else {
          lastSort = { key, dir: key === 'name' ? 'asc' : 'desc' };
        }
        renderTable();
      });
    });
  }

  refreshBtn.addEventListener('click', () => void load());
  filterEl.addEventListener('input', () => renderTable());

  await load();
}

function renderTotals(host: HTMLElement, rows: ReadonlyArray<IntegratorKpiRow>): void {
  let turnover = 0n;
  let ggr = 0n;
  let rounds = 0;
  let players = 0;
  let active = 0;
  let tenantsNeedingReconcile = 0;
  for (const r of rows) {
    turnover += BigInt(r.kpi24h.turnover);
    ggr += BigInt(r.kpi24h.ggr);
    rounds += r.kpi24h.rounds;
    players += r.kpi24h.players;
    if (r.status === 'active') active += 1;
    const rec = r.reconcile;
    if (rec && (rec.unresolvedDrift > 0 || rec.failedCalls > 0)) tenantsNeedingReconcile += 1;
  }
  const reconcileColor = tenantsNeedingReconcile > 0 ? 'var(--warn)' : 'var(--text)';
  // RTP = payout / turnover = 1 − GGR/turnover (GGR = turnover − payout).
  // Provider-wide 24h, derived from the same KPI rows. null at zero turnover.
  const rtp = turnover > 0n ? 1 - Number(ggr) / Number(turnover) : null;
  const rtpPct = rtp === null ? '—' : `${(rtp * 100).toFixed(1)}%`;
  // House wants RTP at/below the ~95% target: green ≤97%, amber 97–100%,
  // red ≥100% (players +EV — house bleeding).
  const rtpColor =
    rtp === null ? 'var(--muted)' : rtp >= 1.0 ? 'var(--error)' : rtp >= 0.97 ? 'var(--warn)' : 'var(--success)';
  host.innerHTML = `
    <div class="kpi"><div class="kpi-label">Integrators</div><div class="kpi-value">${rows.length}</div><div class="kpi-sub muted">${active} active</div></div>
    <div class="kpi"><div class="kpi-label">Total turnover 24h</div><div class="kpi-value">${fmtBig(turnover)}</div></div>
    <div class="kpi"><div class="kpi-label">Total GGR 24h</div><div class="kpi-value">${fmtBig(ggr)}</div></div>
    <div class="kpi"><div class="kpi-label">RTP 24h</div><div class="kpi-value" style="color:${rtpColor}">${rtpPct}</div><div class="kpi-sub muted">target ≈ 95% · per-game in Games → RNG &amp; RTP</div></div>
    <div class="kpi"><div class="kpi-label">Rounds 24h</div><div class="kpi-value">${rounds.toLocaleString()}</div></div>
    <div class="kpi"><div class="kpi-label">Active players 24h</div><div class="kpi-value">${players.toLocaleString()}</div></div>
    <div class="kpi"><div class="kpi-label">Needing reconcile</div><div class="kpi-value" style="color:${reconcileColor}">${tenantsNeedingReconcile}</div><div class="kpi-sub muted">integrators w/ drift or failed calls</div></div>
  `;
}

function renderReachabilityCell(r: IntegratorKpiRow): string {
  const rec = r.reconcile;
  if (!rec) return '<span class="muted">—</span>';
  if (!rec.lastCallAt) return '<span class="muted">no calls yet</span>';
  const age = Date.now() - new Date(rec.lastCallAt).getTime();
  const ageLabel = age < 60_000 ? `${Math.round(age / 1000)}s` : age < 3_600_000 ? `${Math.round(age / 60_000)}m` : `${Math.round(age / 3_600_000)}h`;
  const ok = rec.lastCallStatus === 'confirmed' && age < 5 * 60_000;
  const stale = age >= 5 * 60_000;
  const color = ok ? 'var(--success)' : stale ? 'var(--warn)' : 'var(--error)';
  const latency = rec.lastCallLatencyMs != null ? `${rec.lastCallLatencyMs}ms` : '—';
  return `<span style="display:inline-flex;gap:6px;align-items:center;font-size:12px">
    <span style="width:8px;height:8px;border-radius:50%;background:${color}"></span>
    <span class="muted">${ageLabel} ago · ${latency}</span>
  </span>`;
}

function renderTable_(rows: ReadonlyArray<IntegratorKpiRow>): string {
  if (rows.length === 0) {
    return emptyState({
      icon: 'list',
      title: 'No integrators match',
      hint: 'Adjust the filter or create an integrator from the Integrators page.',
    });
  }
  const headerCell = (key: SortKey, label: string, align: 'left' | 'right' = 'left'): string => {
    const cls = ['ov-th-sort'];
    if (lastSort.key === key) cls.push('active', lastSort.dir);
    return `<th data-sort="${key}" class="${cls.join(' ')}" style="text-align:${align}">${label}</th>`;
  };
  return `<table>
    <thead><tr>
      ${headerCell('name', 'Integrator')}
      <th>Status</th>
      <th>Currency</th>
      ${headerCell('turnover', 'Turnover 24h', 'right')}
      ${headerCell('ggr', 'GGR 24h', 'right')}
      ${headerCell('rtp', 'RTP', 'right')}
      ${headerCell('rounds', 'Rounds', 'right')}
      ${headerCell('players', 'Players', 'right')}
      <th>Reachability</th>
      <th style="text-align:right">Reconcile</th>
    </tr></thead>
    <tbody>${rows
      .map((r) => {
        const rtpPct = (r.kpi24h.rtp * 100).toFixed(1);
        const rec = r.reconcile;
        const reconcileTotal = rec ? rec.unresolvedDrift + rec.failedCalls : 0;
        const reconcileCell = reconcileTotal > 0
          ? `<a href="/wallet-drift" style="color:var(--warn);text-decoration:none;font-weight:600">${reconcileTotal}</a> <span class="muted" style="font-size:11px">(${rec?.unresolvedDrift ?? 0}d · ${rec?.failedCalls ?? 0}f)</span>`
          : '<span class="muted">—</span>';
        return `<tr class="ov-row" data-integrator="${r.id}">
          <td><strong>${escapeHtml(r.name)}</strong> <span class="muted">${escapeHtml(r.slug)}</span></td>
          <td><span class="pill ${r.status === 'active' ? 'live' : 'warn'}">${r.status}</span></td>
          <td><code>${escapeHtml(r.defaultFiatCurrency)}</code></td>
          <td style="text-align:right;font-variant-numeric:tabular-nums">${fmtBig(BigInt(r.kpi24h.turnover))}</td>
          <td style="text-align:right;font-variant-numeric:tabular-nums">${fmtBig(BigInt(r.kpi24h.ggr))}</td>
          <td style="text-align:right;font-variant-numeric:tabular-nums">${r.kpi24h.betCount > 0 ? rtpPct + '%' : '—'}</td>
          <td style="text-align:right;font-variant-numeric:tabular-nums">${r.kpi24h.rounds.toLocaleString()}</td>
          <td style="text-align:right;font-variant-numeric:tabular-nums">${r.kpi24h.players.toLocaleString()}</td>
          <td>${renderReachabilityCell(r)}</td>
          <td style="text-align:right">${reconcileCell}</td>
        </tr>`;
      })
      .join('')}</tbody>
  </table>`;
}

function sortRows(rows: IntegratorKpiRow[], key: SortKey, dir: 'asc' | 'desc'): void {
  const mult = dir === 'asc' ? 1 : -1;
  rows.sort((a, b) => {
    switch (key) {
      case 'name':
        return a.name.localeCompare(b.name) * mult;
      case 'ggr': {
        const av = BigInt(a.kpi24h.ggr);
        const bv = BigInt(b.kpi24h.ggr);
        return (av < bv ? -1 : av > bv ? 1 : 0) * mult;
      }
      case 'turnover': {
        const av = BigInt(a.kpi24h.turnover);
        const bv = BigInt(b.kpi24h.turnover);
        return (av < bv ? -1 : av > bv ? 1 : 0) * mult;
      }
      case 'rounds':
        return (a.kpi24h.rounds - b.kpi24h.rounds) * mult;
      case 'players':
        return (a.kpi24h.players - b.kpi24h.players) * mult;
      case 'rtp':
        return (a.kpi24h.rtp - b.kpi24h.rtp) * mult;
    }
  });
}

function fmtBig(n: bigint): string {
  return n.toLocaleString();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}
